/**
 * Pre-overhaul ("classic") Experiences overview — the homepage users saw before the
 * SC-31250 redesign, recovered verbatim from commit 1f8345e (the parent of the
 * redesign) so both UIs can coexist behind the NEW_UX flag. Rendered by the wrapper in
 * ./index.tsx when NEW_UX is OFF. Keep this in sync only if the old page must change;
 * new work goes in the redesigned overview.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { compact, groupBy, sortBy } from 'lodash';
import { Archive, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../components/AdditionalActionsMenu';
import { CardWithHover } from '../../../../components/CardWitHover';
import { DadoAIBanner } from '../../../../components/DadoAIBanner';
import { Loader } from '../../../../components/Loader';
import { TableSearch } from '../../../../components/Table';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import { useScrollIntoView } from '../../../../hooks/useScrollIntoView';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  duplicateTemplate,
  useExperienceTemplatesByOrgId
} from '../../../../services/experiences/template';
import {
  getOrgIdOrFreeTierSampleContentOrgId,
  isFreeTierOrg
} from '../../../../services/organizationTier';
import {
  hasCurrentUserExperienceLevelScopes,
  hasUserLevelScopes
} from '../../../../services/permissions';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { toSearchTermRegexp } from '../../../../services/user';
import { AnchorField, useAnchorFields } from '../../components/Anchors';
import { CopilotBadge } from '../../components/CopilotBadge';
import { TemplateStatusChip } from '../../components/TemplateStatusChip';
import { TaskTimeline } from '../Details/TaskTimeline';

const ExperienceCard = styled(
  React.forwardRef<
    any,
    React.ComponentProps<typeof CardWithHover> & {
      highlighted?: boolean;
      noPointer?: boolean;
    }
  >(({ highlighted, noPointer, ...props }, ref) => (
    <CardWithHover {...props} ref={ref} />
  ))
)<{ highlighted?: boolean; noPointer?: boolean }>`
  background: ${(p) => p.theme.custom.colors.background.light};
  box-shadow: ${(p) =>
    p.highlighted
      ? `0 0 0 4px ${p.theme.custom.colors.green.w200}!important`
      : undefined};
  border-color: ${(p) =>
    p.highlighted
      ? `${p.theme.custom.colors.green.w300}!important`
      : undefined};

  /* Add styling for noPointer if needed */
  pointer-events: ${(p) => (p.noPointer ? 'none' : 'auto')};
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(3)}px;
  grid-row-gap: ${(p) => p.theme.spacing(3)}px;
`;

const ExperienceItemHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing()}px;
`;

const ExperienceItemTitle = styled('div')`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing()}px;
`;

const ExperienceItem = ({
  tpl,
  linkTo,
  anchorId,
  scrollIntoView,
  onDuplicate,
  anchorFields,
  canCreate
}: {
  tpl: Doc<IExperienceTemplate>;
  linkTo: string;
  anchorId: string;
  scrollIntoView: boolean;
  onDuplicate: (d: Doc<IExperienceTemplate>) => void;
  anchorFields: AnchorField[];
  canCreate: boolean;
}) => {
  const ref = useScrollIntoView(scrollIntoView, { behavior: 'smooth' });
  const { id, org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess } = useSnackbar();

  const options: AdditionActionsMenuOption[] = useMemo(
    () =>
      compact([
        canCreate && {
          key: 'duplicate',
          label: 'Duplicate',
          onClick: () =>
            duplicateTemplate(tpl.data, id).then((d) => {
              onDuplicate(d);
              enqueueSnackbarSuccess(
                t(`Created new draft based on '${tpl.data.title}'`)
              );
            })
        }
      ]),
    [tpl, id, onDuplicate, enqueueSnackbarSuccess, canCreate, t]
  );

  const linkable = !isFreeTierOrg(org);

  const body = (
    <ExperienceCard
      variant="outlined"
      ref={ref}
      id={anchorId}
      data-highlighted={scrollIntoView}
      noPointer={!linkable}
    >
      <ExperienceItemHeader>
        <ExperienceItemTitle>
          <Typography variant="h4" component="h4">
            {tpl.data.adminTitle || tpl.data.title}
          </Typography>
          <TemplateStatusChip status={tpl.data.status} size="small" />
          {tpl.data.aiFeatureUsed && <CopilotBadge size="small" />}
        </ExperienceItemTitle>
        <AdditionalActionsMenu options={options} variant="horizontal" />
      </ExperienceItemHeader>
      <TaskTimeline
        tpl={tpl}
        variant="dense"
        enrollStats={
          tpl.data.status !== ExperienceTemplateStatus.DRAFT &&
          tpl.data.status !== ExperienceTemplateStatus.ROUGH_DRAFT
        }
        anchorFields={anchorFields}
      />
    </ExperienceCard>
  );

  return linkable ? <Link to={linkTo}>{body}</Link> : body;
};

const EmptyStateContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  minHeight: 400,

  '&&': {
    '> *': {
      marginBottom: p.theme.spacing(2),
      textAlign: 'center'
    }
  }
}));

const EmptyState = ({
  canCreate,
  hasAnyTemplatesButNonAccessible
}: {
  canCreate: boolean;
  hasAnyTemplatesButNonAccessible: boolean;
}) => {
  const { ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();
  return (
    <PageBody noTopPadding>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography variant="h1" component="h1">
            {t('Experiences')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          {isBrowser && canCreate && (
            <Link to={ROUTES.experiences.details.create.url()}>
              <Button
                startIcon={<Plus size={16} />}
                variant="contained"
                color="primary"
                fullWidth
              >
                {t('New Experience')}
              </Button>
            </Link>
          )}
        </PageToolbarActions>
      </PageToolbar>
      <EmptyStateContainer>
        <Typography variant="h2" color="primary">
          {hasAnyTemplatesButNonAccessible
            ? t(`You currently don't have access to any experiences.`)
            : t('Your organization doesn’t have any experiences yet')}
        </Typography>
        {canCreate && (
          <Typography variant="body1">
            {t(
              'To create a new experience, click on the button on the top right of the screen'
            )}
          </Typography>
        )}
        {!canCreate && hasAnyTemplatesButNonAccessible && (
          <Typography variant="body1">
            {t('Please contact your account owner to get access.')}
          </Typography>
        )}
      </EmptyStateContainer>
    </PageBody>
  );
};

export const PageExperiencesOverviewClassic = () => {
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const anchorFields = useAnchorFields(org.customUserFields);
  const { ROUTES, getHash, updateHash } = useRoutes();
  const [tpls, loading, error] = useExperienceTemplatesByOrgId(
    getOrgIdOrFreeTierSampleContentOrgId(org)
  );
  const { t } = useTranslation();
  const hash = getHash();

  const [q, setQ] = useState('');

  const [showArchived, setShowArchived] = useState(false);

  const { trackEvent } = useProductAnalytics();
  useEffect(() => {
    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Experiences List Page Loaded',
      Explanation: 'Admin user loads the page listing all experiences'
    });
  }, [trackEvent]);

  const accessibleTpls = useMemo(() => {
    if (!tpls) {
      return null;
    }
    return tpls.filter((tpl) =>
      hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['view'])
    );
  }, [tpls, currentUser]);

  const grouped = useMemo(() => {
    if (!accessibleTpls) {
      return null;
    }
    const re = toSearchTermRegexp(q);
    return groupBy(
      sortBy(
        accessibleTpls.filter((tpl) =>
          (tpl.data.adminTitle || tpl.data.title).match(re)
        ),
        (e) => -e.data.updatedAt.toMillis()
      ),
      (e) => e.data.status
    );
  }, [accessibleTpls, q]);

  if (loading) {
    return <Loader height={500} />;
  }

  const canCreate = hasUserLevelScopes(currentUser, [
    'experience.template.create'
  ]);

  if (tpls && !tpls.length) {
    return (
      <EmptyState
        canCreate={canCreate}
        hasAnyTemplatesButNonAccessible={false}
      />
    );
  }

  if (tpls && tpls.length && accessibleTpls && !accessibleTpls.length) {
    return (
      <EmptyState
        canCreate={canCreate}
        hasAnyTemplatesButNonAccessible={true}
      />
    );
  }

  if (grouped) {
    const active = grouped[ExperienceTemplateStatus.ACTIVE] || [];
    const inactive = grouped[ExperienceTemplateStatus.INACTIVE] || [];
    const drafts = grouped[ExperienceTemplateStatus.DRAFT] || [];
    const roughDrafts = grouped[ExperienceTemplateStatus.ROUGH_DRAFT] || [];
    const archived = grouped[ExperienceTemplateStatus.ARCHIVED] || [];

    return (
      <>
        <DadoAIBanner />
        <PageBody noTopPadding>
          <TeaserBoxFreeTierForExperiences>
            {t(
              'This is a preview of Experiences, a feature which enables you to run personalized, automated HR processes at scale.'
            )}
          </TeaserBoxFreeTierForExperiences>
          <PageToolbar sticky>
            <PageToolbarTitle>
              <Typography variant="h1" component="h1">
                {t('Experiences')}
              </Typography>
            </PageToolbarTitle>
            <PageToolbarActions>
              <TableSearch
                term={q}
                onChange={setQ}
                autoFocus
                placeholder={t('Search by name')}
              />
              {isBrowser && canCreate && (
                <Link to={ROUTES.experiences.details.create.url()}>
                  <Button
                    startIcon={<Plus size={16} />}
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    {t('New Experience')}
                  </Button>
                </Link>
              )}
            </PageToolbarActions>
          </PageToolbar>
          <Section>
            <Grid>
              {active.length
                ? active.map((exp) => (
                    <ExperienceItem
                      key={exp.id}
                      tpl={exp}
                      linkTo={ROUTES.experiences.details.general.url(exp.id)}
                      anchorId={`tpl-${exp.id}`}
                      scrollIntoView={hash.slice(1) === `tpl-${exp.id}`}
                      onDuplicate={(d) => updateHash(`tpl-${d.id}`)}
                      anchorFields={anchorFields}
                      canCreate={canCreate}
                    />
                  ))
                : null}
              {inactive.length
                ? inactive.map((exp) => (
                    <ExperienceItem
                      key={exp.id}
                      tpl={exp}
                      linkTo={ROUTES.experiences.details.general.url(exp.id)}
                      anchorId={`tpl-${exp.id}`}
                      scrollIntoView={hash.slice(1) === `tpl-${exp.id}`}
                      onDuplicate={(d) => updateHash(`tpl-${d.id}`)}
                      anchorFields={anchorFields}
                      canCreate={canCreate}
                    />
                  ))
                : null}
              {drafts.length
                ? drafts.map((exp) => (
                    <ExperienceItem
                      key={exp.id}
                      tpl={exp}
                      linkTo={ROUTES.experiences.details.general.url(exp.id)}
                      anchorId={`tpl-${exp.id}`}
                      scrollIntoView={hash.slice(1) === `tpl-${exp.id}`}
                      onDuplicate={(d) => updateHash(`tpl-${d.id}`)}
                      anchorFields={anchorFields}
                      canCreate={canCreate}
                    />
                  ))
                : null}
              {roughDrafts.length
                ? roughDrafts.map((exp) => (
                    <ExperienceItem
                      key={exp.id}
                      tpl={exp}
                      linkTo={ROUTES.experiences.details.edit.url(exp.id)}
                      anchorId={`tpl-${exp.id}`}
                      scrollIntoView={hash.slice(1) === `tpl-${exp.id}`}
                      onDuplicate={(d) => updateHash(`tpl-${d.id}`)}
                      anchorFields={anchorFields}
                      canCreate={canCreate}
                    />
                  ))
                : null}
              {archived.length && showArchived
                ? archived.map((exp) => (
                    <ExperienceItem
                      key={exp.id}
                      tpl={exp}
                      linkTo={ROUTES.experiences.details.general.url(exp.id)}
                      anchorId={`tpl-${exp.id}`}
                      scrollIntoView={hash.slice(1) === `tpl-${exp.id}`}
                      onDuplicate={(d) => updateHash(`tpl-${d.id}`)}
                      anchorFields={anchorFields}
                      canCreate={canCreate}
                    />
                  ))
                : null}
            </Grid>
          </Section>
          {!!archived.length && (
            <>
              <Button
                variant="text"
                color="primary"
                startIcon={<Archive size={16} />}
                onClick={() => setShowArchived((x) => !x)}
              >
                {showArchived
                  ? t('Hide Archived Experiences')
                  : t('Show Archived Experiences')}
              </Button>
            </>
          )}
        </PageBody>
      </>
    );
  }

  return null;
};
